import { AUDIO_CONFIG } from '../config/constants';
import audioService from '../services/audioService';

/**
 * Generates a binaural beat from two oscillators panned left and right
 *
 * @param {number} freq1 - Frequency for the left ear (carrier)
 * @param {number} freq2 - Frequency for the right ear (carrier + beat)
 * @param {Function} setGainNode - Callback to hand the gain node to the caller
 * @param {number} panWidth - Stereo separation (0-1); below 1 the tones bleed
 *   into both ears and the beat becomes partly monaural
 * @param {number} initialVolume - Volume to fade in to (0.0 to 1.0)
 * @returns {Object} - stop function, pan nodes and oscillators
 */
export default function generateBinauralBeats(
    freq1,
    freq2,
    setGainNode,
    panWidth = AUDIO_CONFIG.DEFAULT_PAN_WIDTH / 100,
    initialVolume = AUDIO_CONFIG.FADE_IN_VOLUME
) {
    const audioCtx = audioService.getContext();
    const gainNode = audioCtx.createGain();

    // Exponential ramps cannot start from 0, so fade in from the minimum gain
    gainNode.gain.value = AUDIO_CONFIG.MIN_GAIN_VALUE;

    const osc1 = audioCtx.createOscillator();
    const osc2 = audioCtx.createOscillator();

    osc1.frequency.setValueAtTime(freq1, audioCtx.currentTime);
    osc2.frequency.setValueAtTime(freq2, audioCtx.currentTime);

    const panNode1 = audioCtx.createStereoPanner();
    const panNode2 = audioCtx.createStereoPanner();

    panNode1.pan.value = -panWidth;
    panNode2.pan.value = panWidth;

    const panNodes = {
        left: panNode1,
        right: panNode2
    };

    osc1.connect(panNode1);
    osc2.connect(panNode2);
    panNode1.connect(gainNode);
    panNode2.connect(gainNode);
    gainNode.connect(audioCtx.destination);

    osc1.start();
    osc2.start();

    setGainNode(gainNode);

    // Smoothly fade in to the requested volume to prevent clicks
    const targetVolume = Math.max(initialVolume, AUDIO_CONFIG.MIN_GAIN_VALUE);
    gainNode.gain.setValueAtTime(AUDIO_CONFIG.MIN_GAIN_VALUE, audioCtx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(targetVolume, audioCtx.currentTime + 0.1);

    return {
        stop: () => {
            // Fade out before stopping to prevent clicks
            const now = audioCtx.currentTime;
            const stopTime = now + 0.1;
            gainNode.gain.cancelScheduledValues(now);
            gainNode.gain.setValueAtTime(gainNode.gain.value, now);
            gainNode.gain.exponentialRampToValueAtTime(AUDIO_CONFIG.MIN_GAIN_VALUE, stopTime);

            osc1.stop(stopTime);
            osc2.stop(stopTime);

            // Disconnect nodes after the fade out; the shared context stays open
            setTimeout(() => {
                osc1.disconnect();
                osc2.disconnect();
                panNode1.disconnect();
                panNode2.disconnect();
                gainNode.disconnect();
            }, 150);
        },
        panNodes: panNodes,
        oscillators: {
            left: osc1,
            right: osc2
        }
    };
}
